import React from "react";
import { createColumnHelper } from "@tanstack/react-table";
import StatusPill from "ui-components/status-pill/statusPill";
import TimestampDisplay from "ui-components/timestamp-display/timestampDisplay";
import DockUserCard from "ui-components/dock-user-card/dockUserCard";
import EquipmentCard from "ui-components/equipment-card/equipmentCard";
import {
  authEventPillVariant,
  humanizeAuthEventType,
} from "app/data/dock-scan/dockLogMappers";
import { AuthAuditEvent } from "app/data/dock-scan/models";
import { DataTableColumnOption } from "ui-components/data-table/dataTableTypes";

const columnHelper = createColumnHelper<AuthAuditEvent>();

export const DOCK_AUTH_AUDIT_LIST_COLUMN_OPTIONS: DataTableColumnOption[] = [
  { id: "createdAt", label: "Time" },
  { id: "eventType", label: "Event" },
  { id: "employeeId", label: "Employee" },
  { id: "equipmentId", label: "Equipment" },
  { id: "terminal", label: "Terminal" },
  { id: "details", label: "Details" },
];

export function getDockAuthAuditListColumns(options?: {
  terminalNumber?: number;
  terminalName?: string;
  terminalAirportCode?: string;
  showEmployee?: boolean;
}) {
  const showEmployee = options?.showEmployee !== false;
  const terminalLabel = [
    options?.terminalAirportCode,
    options?.terminalName,
  ]
    .filter(Boolean)
    .join(" ");

  const cols = [
    columnHelper.accessor("createdAt", {
      id: "createdAt",
      header: "Time",
      cell: (props) => <TimestampDisplay value={props.getValue()} format="local" />,
      size: 140,
      minSize: 120,
    }),
    columnHelper.accessor("eventType", {
      id: "eventType",
      header: "Event",
      cell: (props) => (
        <StatusPill
          label={humanizeAuthEventType(props.getValue())}
          variant={authEventPillVariant(props.getValue())}
        />
      ),
      size: 140,
      minSize: 120,
    }),
    columnHelper.accessor("employeeId", {
      id: "employeeId",
      header: "Employee",
      cell: (props) => {
        const id = props.getValue();
        if (!id) return "—";
        return (
          <DockUserCard
            employeeId={id}
            firstName={props.row.original.employee?.firstName}
            lastName={props.row.original.employee?.lastName}
            terminalNumber={options?.terminalNumber}
            terminalName={options?.terminalName}
            keepTooltipInside={false}
          />
        );
      },
      size: 180,
      minSize: 160,
    }),
    columnHelper.accessor("equipmentId", {
      id: "equipmentId",
      header: "Equipment",
      cell: (props) => {
        const id = props.getValue();
        if (!id) return "—";
        return <EquipmentCard unitNumber={id} keepTooltipInside={false} />;
      },
      size: 120,
      minSize: 100,
    }),
    columnHelper.display({
      id: "terminal",
      header: "Terminal",
      cell: () => terminalLabel || "—",
      size: 140,
      minSize: 120,
    }),
    columnHelper.accessor((row) => row.metadata || row.message, {
      id: "details",
      header: "Details",
      cell: (props) => {
        const row = props.row.original;
        if (row.metadata && Object.keys(row.metadata).length > 0) {
          return (
            <code className="dock-users__mono dock-auth-audit__details">
              {Object.entries(row.metadata)
                .map(([k, v]) => `${k}=${v}`)
                .join(", ")}
            </code>
          );
        }
        return row.message || "—";
      },
      size: 280,
      minSize: 200,
    }),
  ];

  return showEmployee ? cols : cols.filter((col) => col.id !== "employeeId");
}
